import { useEffect, useState } from 'react';
import {
    Button, Card, Checkbox, FieldError, Input, Label, ListBox, ListBoxItem, Modal, Select, TextField
} from '@heroui/react';

import Notice from '../../common/Notice';
import TimeAgo from '../../common/TimeAgo';
import StatusChip, { DeliveryStateChip } from '../../common/StatusChip';
import { LoadMore } from '../../common/LoadMore';
import { SkeletonText } from '../../common/Skeleton';
import webhooksService from '../../../services/webhooks.service';
import { WEBHOOKS, messageOf, SELECT_PLACEHOLDER } from '../../../i18n/el';
import { EXTERNALISABLE_EVENTS } from '../../../../../api/lib/events.js';

// Οθόνη διαχείρισης συνδρομών γεγονότων (ΛΑ-27, §4.6).
//
// Ο μηχανισμός του P2-1 ήταν **πλήρης και ελεγμένος** στον εξυπηρετητή: υπογραφή,
// επαναλήψεις, καταγραφή παραδόσεων, φράχτης εξωτερίκευσης. Έλειπε ο τρόπος να
// ορίσει κανείς συνδρομή χωρίς απευθείας κλήση στο API — δηλαδή η απαίτηση ήταν
// υλοποιημένη και **απρόσιτη**, η κατηγορία Α του Κεφαλαίου 6.
//
// Ο φράχτης είναι το API: το `integration:manage` του Πίνακα 1 ελέγχεται στον
// εξυπηρετητή, και η απόκρυψη της εγγραφής από το μενού είναι **ευχρηστία και όχι
// ασφάλεια**.
//
// ⚠ **Ο ΚΑΤΑΛΟΓΟΣ ΤΩΝ ΤΥΠΩΝ ΔΙΑΒΑΖΕΤΑΙ, ΔΕΝ ΑΠΑΡΙΘΜΕΙΤΑΙ.** Έρχεται από τη
// **μοναδική πηγή** του συστήματος — το ίδιο αρχείο που διαβάζει ο εξυπηρετητής
// για να αποφασίσει τι επιτρέπεται να φύγει. Δεύτερος κατάλογος εδώ θα σήμαινε
// φόρμα που προσφέρει τύπο τον οποίο το API απορρίπτει, ή που κρύβει τύπο τον
// οποίο δέχεται — και τα δύο σιωπηλά, μέχρι να μη φτάσει ένα webhook.
//
// **Τα ΟΝΟΜΑΤΑ** των τύπων ζουν στο αρχείο κειμένων, και δοκιμή επιβεβαιώνει ότι
// υπάρχει όνομα για καθέναν: νέος τύπος χωρίς όνομα θα εμφανιζόταν στη φόρμα με
// το αγγλικό αναγνωριστικό του.

/**
 * **Γιατί μπορεί να μην υπάρχει καμία παράδοση.**
 *
 * ⚠ **Το κενό ήταν ΔΥΟ καταστάσεις με μία εμφάνιση.** «Καμία παράδοση ακόμη»
 * σημαίνει «περίμενε»· «η συνδρομή δεν πυροδοτείται» σημαίνει «δεν πρόκειται να
 * συμβεί με αυτή τη ρύθμιση». Η οθόνη έδειχνε το ίδιο κείμενο και για τα δύο, και
 * ο ελεγκτής του βήματος Ω8 δεν είχε τρόπο να τα ξεχωρίσει χωρίς τερματικό.
 *
 * **Καθαρή συνάρτηση, ώστε να μπορεί να τη ρωτήσει δοκιμή** — η προηγούμενη
 * εκδοχή αυτού του λάθους (οθόνη που φαίνεται σωστή και δεν λειτουργεί) κόστισε
 * ήδη μία μέρα.
 *
 * @returns κατάλογος λόγων, **από τον πιο αποφασιστικό στον πιο γενικό**. Άδειος
 *   δεν είναι ποτέ: ο τελευταίος λόγος —«πυροδοτούν μόνο αυτά τα γεγονότα»—
 *   ισχύει πάντοτε, και είναι ακριβώς η πληροφορία που έλειπε από τον ελεγκτή
 *   που πέρασε **υπάρχοντα** πόρο σε δεύτερη συλλογή και περίμενε παράδοση.
 */
export function emptyDeliveryReasons(subscription, labelOf = (type) => type) {
    const reasons = [];
    const collection = subscription?.collection ?? null;

    if (!collection) reasons.push(WEBHOOKS.deliveriesWhyOrphan);
    if (subscription?.active === false) reasons.push(WEBHOOKS.deliveriesWhyInactive);
    if (collection?.archived === true) reasons.push(WEBHOOKS.deliveriesWhyArchived);
    if (collection?.visibility === 'private') reasons.push(WEBHOOKS.deliveriesWhyPrivate);

    reasons.push(
        WEBHOOKS.deliveriesWhyEvents((subscription?.events ?? []).map(labelOf).join(', '))
    );
    reasons.push(WEBHOOKS.deliveriesWhyAttached);

    return reasons;
}

/** Ο κατάλογος των λόγων, όταν δεν υπάρχει καμία παράδοση. */
function EmptyDeliveries({ subscription }) {
    const reasons = emptyDeliveryReasons(
        subscription,
        (type) => WEBHOOKS.eventLabels[type] ?? type
    );

    return (
        <div className="flex flex-col gap-2">
            <p className="text-sm text-muted">{WEBHOOKS.deliveriesEmpty}</p>
            <p className="text-sm font-medium">{WEBHOOKS.deliveriesWhyEmpty}</p>
            <ul className="flex list-disc flex-col gap-1 ps-5 text-sm text-muted">
                {reasons.map((reason) => (
                    <li key={reason}>{reason}</li>
                ))}
            </ul>
        </div>
    );
}

/**
 * **Η συνθήκη της φόρμας, ως καθαρή συνάρτηση με όνομα.**
 *
 * @returns αντικείμενο με **ένα κλειδί ανά πεδίο που λείπει**, άδειο όταν η φόρμα
 *   είναι πλήρης. Ο άδειος κατάλογος είναι απάντηση, όχι απουσία απάντησης.
 */
export function missingFields({ collection, url, events }) {
    const problems = {};

    if (!collection) problems.collection = WEBHOOKS.needCollection;

    const target = urlProblem(url);
    if (target) problems.url = target;

    if ((events ?? []).length === 0) problems.events = WEBHOOKS.needEvent;

    return problems;
}

/**
 * Τι φταίει στη διεύθυνση, ή `null`.
 *
 * ⚠ **Δεν αντιγράφει τον φράχτη του εξυπηρετητή** — εκείνος είναι ο πραγματικός
 * και απορρίπτει με 422. Εδώ γίνεται ο **χονδρικός** έλεγχος που γλιτώνει έναν
 * κύκλο δικτύου: κενό, κακοσχηματισμένο, ή ρητά τοπικό όνομα. Ο κατάλογος των
 * τοπικών ονομάτων μένει **σκόπιμα μικρός**: δεύτερη πλήρης διατύπωση του
 * κανόνα θα απέκλινε από την πρώτη, και η απόκλιση εδώ σημαίνει φόρμα που
 * δέχεται ό,τι το API αρνείται.
 */
export function urlProblem(value) {
    const text = String(value ?? '').trim();
    if (text === '') return WEBHOOKS.needUrl;

    let parsed;
    try {
        parsed = new URL(text);
    } catch {
        return WEBHOOKS.badUrl;
    }

    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') return WEBHOOKS.badUrl;

    const host = parsed.hostname.toLowerCase();
    const isLocal = host === 'localhost'
        || host.endsWith('.localhost')
        || host.endsWith('.local')
        || host.endsWith('.internal')
        || host === '127.0.0.1'
        || host === '::1';

    return isLocal ? WEBHOOKS.localUrl : null;
}

/**
 * Ένας τύπος γεγονότος, **επιλέξιμος**.
 *
 * ⚠ Χωριστό συστατικό για δύο λόγους, και ο δεύτερος είναι ο σοβαρός: ο χειριστής
 * γίνεται **ονομασμένη συνάρτηση** για τον στατικό φράχτη καλωδίωσης· και τα
 * **υποσυστατικά** της βιβλιοθήκης μπαίνουν σε **ένα** σημείο, ώστε η παράλειψή
 * τους να μην μπορεί να επαναληφθεί σε πέντε αντίγραφα.
 */
function EventCheckbox({ type, isSelected, onToggle }) {
    function handleChange() {
        onToggle(type);
    }

    return (
        <Checkbox isSelected={isSelected} onChange={handleChange}>
            <Checkbox.Content>
                <Checkbox.Control>
                    <Checkbox.Indicator />
                </Checkbox.Control>
                <span className="text-sm">{WEBHOOKS.eventLabels[type] ?? type}</span>
            </Checkbox.Content>
        </Checkbox>
    );
}

/**
 * Μία συνδρομή στον κατάλογο.
 *
 * Χωριστό συστατικό ώστε οι χειριστές της να είναι **ονομασμένες συναρτήσεις**:
 * ο στατικός φράχτης καλωδίωσης διαβάζει τα χαρακτηριστικά ως κείμενο μέχρι το
 * πρώτο `>`, και ένα ενσωματωμένο βέλος κόβει τη σάρωση στη μέση της ετικέτας.
 */
function SubscriptionRow({ subscription, onToggle, onRemove, onDeliveries }) {
    const collection = subscription.collection ?? null;
    const isOrphan = !collection;
    // **Η ανάκληση προηγείται της ορφάνιας στην εμφάνιση**, γιατί είναι η
    // **αιτία**: η συλλογή έφυγε ΚΑΙ γι' αυτό η συνδρομή ανακλήθηκε. Δείχνοντας
    // μόνο «η συλλογή δεν υπάρχει», η οθόνη άφηνε τον διαχειριστή να νομίζει ότι
    // η συνδρομή ζει ακόμη και περιμένει.
    const isRevoked = subscription.revoked === true;

    function handleToggle() {
        onToggle(subscription);
    }

    function handleRemove() {
        onRemove(subscription);
    }

    function handleDeliveries() {
        onDeliveries(subscription);
    }

    return (
        <li className="flex flex-col gap-3 rounded-xl border border-border bg-surface p-4">
            <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="flex min-w-0 flex-col gap-1">
                    <span className="truncate font-medium">{subscription.url}</span>

                    {/* ⚠ **Η ορφανή συνδρομή το ΛΕΕΙ.** Η διαγραφή μιας συλλογής
                        ανακαλεί τα κλειδιά πρόσβασής της αλλά **δεν αγγίζει** τις
                        συνδρομές: εκείνες επιβιώνουν, κρατώντας μυστικό υπογραφής
                        και διεύθυνση τρίτου συστήματος. Δεν στέλνουν τίποτα, γιατί
                        η συλλογή δεν παράγει πια γεγονότα — αλλά ούτε ανακαλούνται.
                        Κενή θέση εδώ θα διαβαζόταν ως σφάλμα φόρτωσης. */}
                    {isRevoked && (
                        <StatusChip icon="fa-solid fa-ban" tone="danger" appearance="outline">
                            {WEBHOOKS.revoked}
                        </StatusChip>
                    )}
                    {isOrphan ? (
                        <StatusChip
                            icon="fa-solid fa-link-slash"
                            tone="danger"
                            appearance="outline"
                            className="self-start"
                        >
                            {WEBHOOKS.orphanCollection}
                        </StatusChip>
                    ) : (
                        <span className="text-sm text-muted">
                            {`${WEBHOOKS.collection}: ${collection.title}`}
                        </span>
                    )}
                </div>

                <StatusChip
                    icon={subscription.active ? 'fa-solid fa-circle-check' : 'fa-solid fa-circle-pause'}
                    tone={subscription.active ? 'teal' : 'neutral'}
                >
                    {subscription.active ? WEBHOOKS.active : WEBHOOKS.inactive}
                </StatusChip>
            </div>

            <span className="flex flex-wrap items-center gap-1">
                {(subscription.events ?? []).map((event) => (
                    <StatusChip key={event} icon="fa-solid fa-bolt" tone="info" appearance="outline">
                        {WEBHOOKS.eventLabels[event] ?? event}
                    </StatusChip>
                ))}
            </span>

            {collection?.visibility === 'private' && (
                <p className="text-xs text-muted">{WEBHOOKS.privateNote}</p>
            )}
            {isRevoked && <p className="text-xs text-muted">{WEBHOOKS.revokedHint}</p>}
            {isOrphan && !isRevoked && <p className="text-xs text-muted">{WEBHOOKS.orphanHint}</p>}

            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted">
                <span>
                    {WEBHOOKS.createdAt}: <TimeAgo date={subscription.createdAt} />
                </span>
                <span>
                    {subscription.lastDelivery ? (
                        <>
                            {WEBHOOKS.lastDelivery}: <TimeAgo date={subscription.lastDelivery} />
                        </>
                    ) : (
                        WEBHOOKS.neverDelivered
                    )}
                </span>
            </div>

            <div className="flex flex-wrap gap-2">
                <Button size="sm" variant="outline" onPress={handleDeliveries}>
                    <i className="fa-solid fa-paper-plane" aria-hidden="true" />
                    {WEBHOOKS.deliveries}
                </Button>
                {/* ⚠ **Η ΑΝΑΚΛΗΜΕΝΗ ΔΕΝ ΕΠΑΝΕΝΕΡΓΟΠΟΙΕΙΤΑΙ.** Το κουμπί
                    **λείπει** αντί να είναι ανενεργό: ανενεργό κουμπί λέει «δεν
                    μπορείς τώρα» και υπονοεί ότι κάποτε θα μπορείς. Η ανάκληση
                    δεν αναιρείται, και η εξήγηση κάθεται από πάνω ως κείμενο. */}
                {!isRevoked && (
                <Button size="sm" variant="outline" onPress={handleToggle}>
                    {subscription.active ? WEBHOOKS.disable : WEBHOOKS.enable}
                </Button>
                )}
                <Button size="sm" variant="ghost" onPress={handleRemove}>
                    {WEBHOOKS.remove}
                </Button>
            </div>
        </li>
    );
}

export default function WebhooksPage() {
    const [subscriptions, setSubscriptions] = useState([]);
    const [meta, setMeta] = useState(null);
    const [collections, setCollections] = useState([]);
    const [isLoading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const [collection, setCollection] = useState('');
    const [url, setUrl] = useState('');
    const [fieldErrors, setFieldErrors] = useState({});
    const [events, setEvents] = useState([]);
    const [isCreating, setCreating] = useState(false);

    const [freshSecret, setFreshSecret] = useState(null);
    const [copied, setCopied] = useState(false);

    const [toRemove, setToRemove] = useState(null);
    const [deliveriesOf, setDeliveriesOf] = useState(null);
    const [deliveries, setDeliveries] = useState([]);
    const [isLoadingDeliveries, setLoadingDeliveries] = useState(false);
    const [retryNote, setRetryNote] = useState('');

    function load(page = 1) {
        setLoading(true);
        return webhooksService.getSubscriptions({ page }).then(
            ({ data, meta: pageMeta }) => {
                setSubscriptions((current) => (page === 1 ? data : [...current, ...data]));
                setMeta(pageMeta);
                setLoading(false);
            },
            (apiError) => {
                setError(messageOf(apiError));
                setLoading(false);
            }
        );
    }

    /** Ο χειριστής της επόμενης σελίδας, ως **όνομα**: ενσωματωμένο βέλος μέσα
     *  σε χαρακτηριστικό JSX κόβει τη σάρωση του στατικού ελέγχου καλωδίωσης. */
    function loadMore() {
        load((meta?.page ?? 1) + 1);
    }

    useEffect(() => {
        load();
        webhooksService.getCollections().then(
            ({ data }) => setCollections(data),
            () => {}
        );
    }, []);

    // Το θόλωμα του πεδίου διεύθυνσης: ο έλεγχος προορισμού τρέχει **εδώ** και
    // στην υποβολή. Ονομασμένη συνάρτηση, όχι ενσωματωμένο βέλος.
    function checkUrl() {
        const problem = urlProblem(url);
        setFieldErrors((current) => ({ ...current, url: problem ?? undefined }));
    }

    function toggleEvent(type) {
        // Η επιλογή τύπου καθαρίζει το μήνυμα του **δικού της** πεδίου: μήνυμα
        // που μένει αφού ο χρήστης το διόρθωσε είναι ψευδές.
        setFieldErrors((current) => ({ ...current, events: undefined }));
        setEvents((current) =>
            current.includes(type) ? current.filter((other) => other !== type) : [...current, type]
        );
    }

    /**
     * Τι λείπει από τη φόρμα, **ως καθαρή συνάρτηση**.
     *
     * ⚠ Εξαγόμενη επίτηδες: η συνθήκη «μπορεί να υποβληθεί;» ήταν ενσωματωμένη σε
     * μία έκφραση μέσα στην απόδοση, και **καμία δοκιμή δεν μπορούσε να τη
     * ρωτήσει**. Η οθόνη περνούσε τον έλεγχο απόδοσης ενώ η φόρμα δεν
     * λειτουργούσε καθόλου, γιατί ο έλεγχος ρωτούσε «εμφανίζεται το κείμενο;»
     * και όχι «μπορεί να υποβληθεί;». Καθαρή συνάρτηση με όνομα απαντά και στα
     * δύο ερωτήματα χωρίς περιβάλλον περιηγητή.
     *
     * Ο έλεγχος του **προορισμού** μένει επίσης εδώ, δηλαδή τρέχει στην υποβολή
     * και στο θόλωμα — **ποτέ σε κάθε πλήκτρο**: μισοπληκτρολογημένη διεύθυνση
     * είναι πάντοτε άκυρη, και μήνυμα που εμφανίζεται στον τρίτο χαρακτήρα
     * εκπαιδεύει τον χρήστη να το αγνοεί.
     */
    function create(submitEvent) {
        submitEvent.preventDefault();

        const missing = missingFields({ collection, url, events });
        setFieldErrors(missing);
        if (Object.keys(missing).length > 0) return;

        setCreating(true);
        setError('');
        setCopied(false);

        webhooksService
            .createSubscription({ collection, url: url.trim(), events })
            .then(
                (created) => {
                    // Το μυστικό υπάρχει **μόνο** σε αυτή την απόκριση.
                    setFreshSecret(created.secret);
                    setUrl('');
                    setEvents([]);
                    setFieldErrors({});
                    setCreating(false);
                    load();
                },
                (apiError) => {
                    setError(messageOf(apiError));
                    setCreating(false);
                }
            );
    }

    function toggleActive(subscription) {
        setError('');
        webhooksService.setActive(subscription.id, !subscription.active).then(
            () => load(),
            (apiError) => setError(messageOf(apiError))
        );
    }

    function remove() {
        const target = toRemove;
        setToRemove(null);
        setError('');

        webhooksService.deleteSubscription(target.id).then(
            () => load(),
            (apiError) => setError(messageOf(apiError))
        );
    }

    function openDeliveries(subscription) {
        setDeliveriesOf(subscription);
        setDeliveries([]);
        setRetryNote('');
        setLoadingDeliveries(true);

        webhooksService.getDeliveries(subscription.id).then(
            ({ data }) => {
                setDeliveries(data);
                setLoadingDeliveries(false);
            },
            (apiError) => {
                setError(messageOf(apiError));
                setLoadingDeliveries(false);
            }
        );
    }

    function closeDeliveries() {
        setDeliveriesOf(null);
        setDeliveries([]);
    }

    function retry(delivery) {
        setRetryNote('');
        webhooksService.retryDelivery(deliveriesOf.id, delivery.id).then(
            () => setRetryNote(WEBHOOKS.retryQueued),
            (apiError) => setError(messageOf(apiError))
        );
    }

    function copySecret() {
        navigator.clipboard?.writeText(freshSecret);
        setCopied(true);
    }

    function hideSecret() {
        setFreshSecret(null);
        setCopied(false);
    }

    function dismissError() {
        setError('');
    }


    return (
        <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-1">
                <h2 className="text-xl font-bold">{WEBHOOKS.title}</h2>
                <p className="text-sm text-muted">{WEBHOOKS.intro}</p>
            </div>

            {error && (
                <Notice status="warning" title={WEBHOOKS.title} onDismiss={dismissError}>
                    {error}
                </Notice>
            )}

            <Card>
                <Card.Header>
                    <Card.Title>{WEBHOOKS.create}</Card.Title>
                </Card.Header>
                <Card.Content>
                    <form onSubmit={create} noValidate className="flex flex-col gap-4">
                        <Select placeholder={SELECT_PLACEHOLDER} selectedKey={collection} onSelectionChange={setCollection}>
                            <Label>{WEBHOOKS.collection}</Label>
                            <Select.Trigger>
                                <Select.Value />
                                <Select.Indicator />
                            </Select.Trigger>
                            <Select.Popover>
                                <ListBox items={collections}>
                                    {(item) => <ListBoxItem id={item.id}>{item.title}</ListBoxItem>}
                                </ListBox>
                            </Select.Popover>
                            <p className="text-xs text-muted">{WEBHOOKS.collectionHint}</p>
                            {fieldErrors.collection && (
                                <p className="text-sm text-danger" role="alert">{fieldErrors.collection}</p>
                            )}
                        </Select>

                        <TextField value={url} onChange={setUrl} isInvalid={Boolean(fieldErrors.url)}>
                            <Label>{WEBHOOKS.url}</Label>
                            <Input inputMode="url" placeholder="https://" onBlur={checkUrl} />
                            {fieldErrors.url && <FieldError>{fieldErrors.url}</FieldError>}
                            {/* Ο πραγματικός έλεγχος του προορισμού γίνεται στον
                                εξυπηρετητή, στο σχήμα της διαδρομής: διεύθυνση προς
                                το ίδιο το μηχάνημα ή εσωτερικό δίκτυο απορρίπτεται
                                με 422 **κατά την καταχώρηση**, όχι αργότερα ως
                                αποτυχία παράδοσης. */}
                            <p className="text-xs text-muted">{WEBHOOKS.urlHint}</p>
                        </TextField>

                        <fieldset className="flex flex-col gap-2 rounded-xl border border-border p-4">
                            <legend className="px-2 text-sm font-semibold">{WEBHOOKS.events}</legend>
                            <p className="text-xs text-muted">{WEBHOOKS.eventsHint}</p>

                            {/* ⚠ **ΤΑ ΥΠΟΣΥΣΤΑΤΙΚΑ ΔΕΝ ΕΙΝΑΙ ΔΙΑΚΟΣΜΗΣΗ.**
                                Γραμμένο ως `<Checkbox>{κείμενο}</Checkbox>`, το
                                συστατικό αποδίδει **`<div>` με κείμενο μέσα**:
                                κανένα `<input>`, καμία `<label>`, κανένα
                                στοιχείο ελέγχου. Δεν υπάρχει τι να πατηθεί, δεν
                                δέχεται εστίαση, και το `onChange` **δεν
                                καλείται ποτέ** — η βιβλιοθήκη το συνδέει στο
                                κρυφό πεδίο που ζει μέσα στο `Checkbox.Content`.

                                Αποτέλεσμα: πέντε γραμμές κειμένου που μοιάζουν
                                με κατάλογο επιλογών και **δεν είναι**. Κανένα
                                σφάλμα, καθαρή κονσόλα, πράσινη σουίτα. */}
                            {EXTERNALISABLE_EVENTS.length === 0 ? (
                                // Δεν συνέβη ποτέ —ο κατάλογος έχει πέντε— αλλά
                                // κενή περιοχή χωρίς λέξη είναι ακριβώς το «κενό
                                // που διαβάζεται ως απάντηση». Εδώ η άδεια
                                // περιοχή θα σήμαινε «δεν υπάρχουν τύποι», που
                                // δεν είναι ποτέ αλήθεια.
                                <Notice status="danger" title={WEBHOOKS.noEventTypes}>
                                    {WEBHOOKS.noEventTypesHint}
                                </Notice>
                            ) : (
                                EXTERNALISABLE_EVENTS.map((type) => (
                                    <EventCheckbox
                                        key={type}
                                        type={type}
                                        isSelected={events.includes(type)}
                                        onToggle={toggleEvent}
                                    />
                                ))
                            )}

                            {/* Το μήνυμα **δίπλα στο πεδίο που φταίει**, όπως σε
                                κάθε άλλη φόρμα του συστήματος. Ήταν το μόνο που
                                έλειπε για να φανεί αμέσως το πρόβλημα. */}
                            {fieldErrors.events && (
                                <p className="text-sm text-danger" role="alert">{fieldErrors.events}</p>
                            )}
                        </fieldset>

                        {/* ⚠ **ΕΝΕΡΓΟ ΠΑΝΤΟΤΕ.** Το ανενεργό κουμπί έλεγε την
                            αλήθεια —η φόρμα ήταν όντως ελλιπής— αλλά η αλήθεια
                            δεν ήταν ορατή **πουθενά**: ο χρήστης έβλεπε ένα
                            κουμπί που δεν πατιέται και καμία ένδειξη για το τι
                            λείπει. Η επικύρωση τρέχει στην **υποβολή** και το
                            μήνυμα κάθεται δίπλα στο πεδίο. */}
                        <Button type="submit" isPending={isCreating}>
                            {isCreating ? WEBHOOKS.creating : WEBHOOKS.create}
                        </Button>
                    </form>
                </Card.Content>
            </Card>

            {/* ⚠ **ΤΟ ΜΥΣΤΙΚΟ, ΜΙΑ ΦΟΡΑ.** Υπό συνθήκη κατά την D26: το `isOpen`
                γίνεται ψευδές από πηγή **εκτός** του διαλόγου —το κουμπί που το
                κρύβει— και το `Modal.Root` κλείνει μόνο όταν το κλείσιμο το ξεκινά
                το ίδιο. Χωρίς την αποπροσάρτηση ο διάλογος δεν κλείνει ποτέ. */}
            {freshSecret && (
                <Modal.Root isOpen>
                    <Modal.Backdrop>
                        <Modal.Container size="sm">
                            <Modal.Dialog>
                        <Modal.Header>
                            <Modal.Heading>{WEBHOOKS.secretTitle}</Modal.Heading>
                        </Modal.Header>
                        <Modal.Body className="flex flex-col gap-3">
                            <Notice status="warning" title={WEBHOOKS.secretWarning} />
                            <code className="break-all rounded-lg border border-border bg-surface p-3 text-sm">
                                {freshSecret}
                            </code>
                            <p className="text-xs text-muted">{WEBHOOKS.secretUse}</p>
                        </Modal.Body>
                        <Modal.Footer>
                            <Button variant="outline" onPress={copySecret}>
                                <i className="fa-solid fa-copy" aria-hidden="true" />
                                {copied ? WEBHOOKS.copied : WEBHOOKS.copy}
                            </Button>
                            <Button onPress={hideSecret}>{WEBHOOKS.close}</Button>
                        </Modal.Footer>
                    </Modal.Dialog>
                        </Modal.Container>
                    </Modal.Backdrop>
                </Modal.Root>
            )}

            <Card>
                <Card.Header>
                    <Card.Title>{WEBHOOKS.listTitle}</Card.Title>
                </Card.Header>
                <Card.Content className="flex flex-col gap-3">
                    {isLoading && subscriptions.length === 0 && <SkeletonText />}
                    {!isLoading && !error && subscriptions.length === 0 && (
                        <p className="text-sm text-muted">{WEBHOOKS.empty}</p>
                    )}

                    <ul className="flex flex-col gap-3">
                        {subscriptions.map((subscription) => (
                            <SubscriptionRow
                                key={subscription.id}
                                subscription={subscription}
                                onToggle={toggleActive}
                                onRemove={setToRemove}
                                onDeliveries={openDeliveries}
                            />
                        ))}
                    </ul>

                    <LoadMore
                        loaded={subscriptions.length}
                        meta={meta}
                        isLoading={isLoading}
                        onLoadMore={loadMore}
                        noun={WEBHOOKS.subscriptionsNoun}
                    />
                </Card.Content>
            </Card>

            {toRemove && (
                <Modal.Root isOpen>
                    <Modal.Backdrop>
                        <Modal.Container size="sm">
                            <Modal.Dialog>
                        <Modal.Header>
                            <Modal.Heading>{WEBHOOKS.removeTitle}</Modal.Heading>
                        </Modal.Header>
                        <Modal.Body>
                            {/* ΜΛΑ-03: η επιβεβαίωση ονομάζει **τι σταματά**, με τη
                                διεύθυνση μέσα της. «Είστε σίγουροι;» δεν λέει τίποτα
                                σε οθόνη με πολλές συνδρομές. */}
                            <p className="text-sm">{WEBHOOKS.removeWarning(toRemove)}</p>
                        </Modal.Body>
                        <Modal.Footer>
                            <Button variant="ghost" onPress={() => setToRemove(null)}>
                                {WEBHOOKS.cancel}
                            </Button>
                            <Button onPress={remove}>{WEBHOOKS.removeConfirm}</Button>
                        </Modal.Footer>
                    </Modal.Dialog>
                        </Modal.Container>
                    </Modal.Backdrop>
                </Modal.Root>
            )}

            {deliveriesOf && (
                <Modal.Root isOpen>
                    <Modal.Backdrop>
                        <Modal.Container size="sm">
                            <Modal.Dialog>
                        <Modal.Header>
                            <Modal.Heading>{WEBHOOKS.deliveriesOf(deliveriesOf.url)}</Modal.Heading>
                        </Modal.Header>
                        <Modal.Body className="flex flex-col gap-3">
                            {retryNote && <Notice status="info" title={retryNote} />}
                            {isLoadingDeliveries && <SkeletonText />}
                            {!isLoadingDeliveries && !error && deliveries.length === 0 && (
                                <EmptyDeliveries subscription={deliveriesOf} />
                            )}

                            <ul className="flex flex-col gap-3">
                                {deliveries.map((delivery) => (
                                    <li
                                        key={delivery.id}
                                        className="flex flex-col gap-2 rounded-lg border border-border p-3"
                                    >
                                        <div className="flex flex-wrap items-center gap-2">
                                            <DeliveryStateChip state={delivery.status} />
                                            <span className="text-sm">
                                                {WEBHOOKS.eventLabels[delivery.event] ?? delivery.event}
                                            </span>
                                            <TimeAgo date={delivery.createdAt} className="text-xs text-muted" />
                                        </div>

                                        <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted">
                                            <span>{WEBHOOKS.attemptsOf((delivery.attempts ?? []).length)}</span>
                                            {delivery.lastError && (
                                                <span className="text-danger">
                                                    {`${WEBHOOKS.failureReason}: ${delivery.lastError}`}
                                                </span>
                                            )}
                                        </div>

                                        {delivery.status === 'failed' && (
                                            <Button
                                                size="sm"
                                                variant="outline"
                                                className="self-start"
                                                onPress={() => retry(delivery)}
                                            >
                                                {WEBHOOKS.retry}
                                            </Button>
                                        )}
                                    </li>
                                ))}
                            </ul>

                            {/* Το όριο, δηλωμένο **στην οθόνη** και όχι μόνο σε σχόλιο:
                                ο χρήστης πρέπει να ξέρει ότι το φορτίο δεν λείπει
                                κατά λάθος. */}
                            <p className="text-xs text-muted">{WEBHOOKS.deliveriesNote}</p>
                        </Modal.Body>
                        <Modal.Footer>
                            <Button onPress={closeDeliveries}>{WEBHOOKS.close}</Button>
                        </Modal.Footer>
                    </Modal.Dialog>
                        </Modal.Container>
                    </Modal.Backdrop>
                </Modal.Root>
            )}
        </div>
    );
}
